import * as React from 'react';
import API, { GenericAddResponse } from "../../common/API";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router-dom";
import { FindResponse } from '../../common/Type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserCourseDTO, UserLessonDTO, CourseStatus, LessonType, LessonStep } from '../../common/TrainingTypes';
import { VideoLessonComponent } from './VideoLesson';
import { QuizComponent } from './Quiz';
import "./LessonShell.scss";
import { SoftwareLessonComponent } from './SoftwareLesson';
import { HardwareLessonComponent } from './HardwareLesson';
import { LinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';

export function LessonShellComponent() {
    const auth = useAuth();
    const { t } = useTranslation()
    const navigate = useNavigate();
    const { courseId, lessonId } = useParams();
    const [course, setCourse] = React.useState<UserCourseDTO>();
    const [activeLesson, setActiveLesson] = React.useState<UserLessonDTO>();
    const [showQuiz, setShowQuiz] = React.useState<boolean>();
    const [showStepQuiz, setShowStepQuiz] = React.useState<boolean>(false);

    const [stepDone, setStepDone] = React.useState<boolean>();


    const [activeStep, setActiveStep] = React.useState<LessonStep>();

    const setLesson = async (lesson: UserLessonDTO) => {
        // Start the lesson if required
        if (lesson.userLessonResultId === 0) {
            const response: GenericAddResponse | null = await API.put("/api/v1/training/lessons/" + lesson.id, {}, auth);
            if (response == null)
                return;
            lesson.startedOn = new Date();
            lesson.userLessonResultId = response.id;
        }
        setActiveLesson(lesson)
        setActiveStep(lesson.steps[0])
    }

    const selectLesson = (lesson: UserLessonDTO) => {
        setActiveLesson(lesson);
        setActiveStep(lesson.steps[0])
    }

    const selectStep = (step: LessonStep) => {
        if (course == null || course.lessons == null)
            return;

        const lessons: UserLessonDTO[] = course.lessons.filter((e) => e.steps.some((f) => f.id === step.id));
        if (lessons.length === 0)
            return;

        setActiveLesson(lessons[0]);
        setActiveStep(step);
    }

	React.useEffect(() => {
		const getCourse = async () => {
			const response: FindResponse<UserCourseDTO> | null = await API.get("/api/v1/training/courses/" + courseId, auth);
			if (response != null) {
                setCourse(response.result)

				if (response.result.lessons != null) {
                    if (response.result.status !== CourseStatus.InProgress) {
                        // Set active lesson to id if specified in route
                        if (lessonId !== undefined && parseInt(lessonId, 10) > 0) {
                            const res = response.result.lessons.filter((e) => e.id === parseInt(lessonId, 10))
                            if (res.length > 0) {
                                console.log('set selection');
                                setActiveLesson(res[0]);
                                setActiveStep(res[0].steps[0])
                                return;
                            }
                        }
                    } else {
                        // Look for first lesson that hasn't been completed
                        for (var lesson of response.result.lessons) {
                            if (lesson.quizEnd == null) {
                                setLesson(lesson)
                                break;
                            }
                        }
                    }
				}
			}
		}

		getCourse()
	}, [auth, courseId, lessonId])

	const nextStep = async () => {
		if (activeLesson == null || activeStep == null)
            return;

        if (activeStep.quizQuestions != null && activeStep.quizQuestions.length > 0 && !showStepQuiz) {
            setShowStepQuiz(true)
        } else if (activeStep === activeLesson.steps[activeLesson.steps.length - 1]) {
			setShowQuiz(true);
			activeLesson.quizStart = new Date();
		} else {
			setActiveStep(activeLesson.steps[activeLesson.steps.indexOf(activeStep) + 1])
		}
	}

	const completeQuiz = async (grade: number) => {
		if (activeLesson == null || course == null || course.lessons == null)
            return;

        if (showStepQuiz) {
            nextStep()
            setShowStepQuiz(false)
            return
        }

		setShowQuiz(false);
		activeLesson.quizPercent = grade
		activeLesson.quizEnd = new Date();

		let nextLesson: UserLessonDTO | null = null;
		for (var lesson of course.lessons) {
			if (lesson.quizEnd == null) {
				nextLesson = lesson;
				break;
			}
		}

		if (nextLesson == null) {
			navigate("/training/course/" + course.id);
		} else {
			setLesson(nextLesson);
		}
	}

	return (
		<div>
			<>
                <h1 className="mb-3">
                    <FontAwesomeIcon icon="graduation-cap" className="me-2" />
                    <LinkContainer to="/training/courses" style={{ cursor: 'pointer' }}>
                        <span style={{ cursor: 'pointer' }}>{t('training:training')}</span>
                    </LinkContainer>
                    <FontAwesomeIcon icon="chevron-right" className="mx-2" />
                    <LinkContainer to={"/training/course/" + courseId} style={{ cursor: 'pointer' }}>
                        <span>{course?.name}</span>
                    </LinkContainer>
                    <FontAwesomeIcon icon="chevron-right" className="mx-2" />
                    <span>{activeLesson?.name}</span>
				</h1>
				<div className="row">
					<div className="col-md-4 col-xl-3">
						<div className="card">
							<div className="card-header">
                                <h5 className="card-title mb-0">{t('training:lessons')}</h5>
							</div>
							<div className="card-body">
								<ol className="timeline mt-2 mb-0">
									{course?.lessons?.map((e) => {
										return (
                                            <li className={"timeline-item " + (e.quizEnd != null ? "done" : e.startedOn == null ? "inactive" : "")} key={e.id}>
                                                {course.status !== CourseStatus.InProgress ?
                                                    <a className={(activeLesson === e ? "active" : "")} onClick={function () { selectLesson(e) }}>{e.name}</a> :
                                                    <strong className={(activeLesson === e ? "active" : "")}>{e.name}</strong>}
                                                {e.quizEnd != null ? <span className="float-end badge bg-success">{e.quizPercent}%</span> : <></>}
												<p className="mb-0">{e.description}</p>
												{activeLesson === e ?
													<ul className="timeline mb-0" style={{ listStyleType: 'none' }}>
														{e.steps.map((e) => {
                                                            return (
                                                                <li key={e.id} className="timeline-item mb-3">
                                                                    {course.status !== CourseStatus.InProgress ?
                                                                        <a className={activeStep === e ? "active" : ""} onClick={function () { selectStep(e) }}>{e.name}</a> :
                                                                        <strong className={activeStep === e ? "active" : ""}>{e.name}</strong>}
                                                                </li>
															)
														})}
													</ul> : <></>}
											</li> 
										)
									})}
								</ol>
							</div>
						</div>
                    </div>

                    <div className="col-md-8 col-xl-9">
                        {showQuiz && course != null && activeLesson != null ? <QuizComponent completeQuiz={completeQuiz} userLessonResultId={activeLesson.userLessonResultId} questions={null} /> :
                            showStepQuiz && course != null && activeStep != null && activeStep.quizQuestions != null ? <QuizComponent completeQuiz={completeQuiz} userLessonResultId={null} questions={activeStep.quizQuestions} /> :
                            activeLesson != null && course != null && activeStep != null && activeStep.type === LessonType.Video ? <VideoLessonComponent course={course} lesson={activeLesson} step={activeStep} stepDone={setStepDone} /> :
                            activeLesson != null && course != null && activeStep != null && activeStep.type === LessonType.Hardware ? <HardwareLessonComponent course={course} lesson={activeLesson} step={activeStep} stepDone={setStepDone} /> :
                            activeLesson != null && course != null && activeStep != null && activeStep.type === LessonType.Software ? <SoftwareLessonComponent course={course} lesson={activeLesson} step={activeStep} stepDone={setStepDone} /> :
                             <></>}

                        {course !== undefined && course.status === 1 && !showQuiz && !showStepQuiz ?
                            <button type="button" className={"btn btn-primary w-100 " + (!stepDone ? "disabled" : "")} onClick={nextStep}>
                                {activeStep != null && activeLesson != null && activeStep === activeLesson.steps[activeLesson.steps.length - 1] ? t('training:startQuiz') : t('common:next')}
                        </button> : <></>}

                    </div>
                </div>
			</>
		</div>
	)
}